import FirestoreCollection from "helpers/firebase/firestore/firestore_collection";

const resolveSubCollectionDefaultData = (defaultData, model) => {
	if (typeof defaultData === "function") {
		return defaultData(model);
	}

	return defaultData || {};
};

export default class CoreModel {
	static SUB_COLLECTIONS = {};

	constructor(id){
		this.id = id
	}

	isValid(){
		return true
	}

	/**
	 * Returns a FirestoreCollection for a top-level collection.
	 * Usage: static collection = CoreModel.getCollection("Workflows", MyModel);
	 *
	 * @param {string} collectionName - e.g. "Workflows"
	 * @param {Function} modelClass   - the model class for the collection
	 * @param {Array} preConditions   - optional pre-conditions
	 * @returns {FirestoreCollection}
	 */
	static getCollection(collectionName, modelClass, preConditions = []) {
		return new FirestoreCollection(collectionName, modelClass, preConditions);
	}

	/**
	 * Returns a FirestoreCollection scoped to a subcollection under a parent document.
	 * Usage: MyModel.getSubCollection(parentId)
	 *
	 * @param {string} parentCollection  - e.g. "Workflows"
	 * @param {string} parentId          - the parent document id
	 * @param {string} subCollectionName - e.g. "layout"
	 * @param {Function} modelClass      - the model class for the subcollection
	 * @returns {FirestoreCollection}
	 */
	static getSubCollection(parentCollection, parentId, subCollectionName, modelClass) {
		const path = `${parentCollection}/${parentId}/${subCollectionName}`;
		return new FirestoreCollection(path, modelClass);
	}

	static getSubCollectionDefinitions() {
		return this.SUB_COLLECTIONS || {};
	}

	static getSubCollectionDefinition(key) {
		return this.getSubCollectionDefinitions()?.[key] || null;
	}

	async loadSubCollection(key) {
		const definition = this.constructor.getSubCollectionDefinition?.(key);

		if (!definition) {
			return null;
		}

		const {
			property = key,
			parentCollection,
			subCollectionName,
			docId = "config",
			modelClass,
			createCollection,
			defaultData = {},
		} = definition;

		if (!modelClass) {
			throw new Error(`Missing modelClass for subcollection "${key}" in ${this.constructor.name}`);
		}

		const buildDefaultModel = () => new modelClass(
			docId,
			resolveSubCollectionDefaultData(defaultData, this)
		);

		if (!this.id) {
			this[property] = buildDefaultModel();
			return this[property];
		}

		const collection = typeof createCollection === "function"
			? createCollection(this.id, this)
			: this.constructor.getSubCollection(parentCollection, this.id, subCollectionName, modelClass);

		let loadedModel = null;
		await collection.get(docId, (model) => {
			loadedModel = model;
		});

		this[property] = loadedModel || buildDefaultModel();
		return this[property];
	}
}
